import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, CircularProgress, InputAdornment, Stack, TextField, Typography, Unstable_Grid2 as Grid
} from '@mui/material';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';
import { MY_INTAKE, SUBMIT_INTAKE } from 'api/graphql/nutrition';

const NUM_FIELDS = ['weight', 'height', 'age'];
const TEXT_FIELDS = ['work_activity', 'goal', 'rest'];
const AREA_FIELDS = [
  'food_habits', 'preferences', 'cheat_meals', 'supplements', 'dietary_restrictions',
  // Cirugías y condiciones de salud: van al final porque condicionan el entrenamiento entero, así
  // que el entrenador tiene que leerlas junto con las restricciones, no perdidas entre los hábitos.
  'medical_conditions'
];
const ALL_FIELDS = [...NUM_FIELDS, ...TEXT_FIELDS, ...AREA_FIELDS];

const ADORNMENTS = { weight: 'kg', height: 'm', age: '' };

/**
 * Formulario de admisión del cliente (peso, estatura, edad, hábitos…). Lo que pidió la
 * entrenadora; alimenta el plan específico y los recetarios.
 */
function IntakeForm() {
  const { t } = useTranslation(['nutrition']);
  const dispatch = useDispatch();
  const { data, loading } = useQuery(MY_INTAKE, { fetchPolicy: 'cache-and-network' });
  const [submitIntake, { loading: saving }] = useMutation(SUBMIT_INTAKE);
  const [values, setValues] = useState({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data?.myIntake) {
      const next = {};
      ALL_FIELDS.forEach((f) => { next[f] = data.myIntake[f] ?? ''; });
      setValues(next);
    }
  }, [data]);

  const setField = (field) => (e) => { setValues((p) => ({ ...p, [field]: e.target.value })); setSaved(false); };

  const handleSave = async () => {
    const input = {};
    ALL_FIELDS.forEach((f) => {
      const v = values[f];
      if (v === '' || v == null) return;
      input[f] = NUM_FIELDS.includes(f) ? Number(v) : v;
    });
    try {
      await submitIntake({ variables: { input } });
      setSaved(true);
      dispatch(uiSlice.actions.enqueueSnackbar(createSnack({
        key: `intake-saved-${Date.now()}`,
        variant: 'success',
        message: t('intakeSaved', { ns: 'nutrition', defaultValue: 'Datos guardados' })
      })));
    } catch (e) {
      // El errorHandler de Apollo ya muestra un mensaje amigable (nunca vacío).
      setSaved(false);
    }
  };

  if (loading && !data) {
    return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} sx={{ color: 'secondary.main' }} /></Box>;
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {t('intakeIntro', { ns: 'nutrition', defaultValue: 'Cuéntale a tu entrenador sobre ti para personalizar tu plan.' })}
      </Typography>
      <Grid container spacing={2.5}>
        {NUM_FIELDS.map((f) => (
          <Grid xs={12} sm={4} key={f}>
            <TextField
              fullWidth size="small" type="number" value={values[f] ?? ''} onChange={setField(f)}
              label={t(f, { ns: 'nutrition' })}
              InputProps={ADORNMENTS[f] ? { endAdornment: <InputAdornment position="end">{ADORNMENTS[f]}</InputAdornment> } : undefined}
            />
          </Grid>
        ))}
        {TEXT_FIELDS.map((f) => (
          <Grid xs={12} sm={4} key={f}>
            <TextField fullWidth size="small" value={values[f] ?? ''} onChange={setField(f)} label={t(f, { ns: 'nutrition' })} />
          </Grid>
        ))}
        {AREA_FIELDS.map((f) => (
          <Grid xs={12} sm={6} key={f}>
            <TextField fullWidth multiline minRows={2} value={values[f] ?? ''} onChange={setField(f)} label={t(f, { ns: 'nutrition' })} />
          </Grid>
        ))}
      </Grid>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 3 }}>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700 }} startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}>
          {t('saveIntake', { ns: 'nutrition', defaultValue: 'Guardar mis datos' })}
        </Button>
        {saved && <Typography variant="body2" sx={{ color: 'secondary.main', fontWeight: 700 }}>{t('intakeSaved', { ns: 'nutrition', defaultValue: 'Datos guardados ✓' })}</Typography>}
      </Stack>
    </Box>
  );
}

export default IntakeForm;
